import ApiManager from '../ApiManager/ApiManager';

/**
 * Gets a presigned URL for downloading a file and determines its MIME type from the file extension
 *
 * @param {string} fileName - The name of the file to download
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise<{url: string, contentType: string, fileName: string, expires: string}>}
 */
export const getPresignedDownloadUrl = async (fileName, onProgress) => {
  if (!fileName) {
    throw new Error("No file name provided");
  }

  console.log('Getting presigned URL for:', fileName);

  // Update progress if callback provided
  if (onProgress) onProgress(20);

  try {
    // Get a presigned URL for direct download
    console.log('Calling API to get presigned URL');
    const response = await ApiManager.storageGetPresignedDownloadUrl(fileName);

    if (onProgress) onProgress(60);

    if (!response.ok) {
      console.error('Failed to generate presigned URL:', response.status, response.statusText);
      throw new Error(`Failed to generate presigned URL: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Presigned URL API response:', result);

    if (!result.url) {
      console.error('No URL in presigned URL response');
      throw new Error('No URL in presigned URL response');
    }

    if (onProgress) onProgress(90);

    // Determine content type from file extension
    const contentType = getMimeTypeFromFileName(fileName);
    console.log('Determined content type from extension:', contentType);

    return {
      url: result.url,
      contentType,
      fileName: result.fileName || fileName,
      expires: result.expires
    };
  } catch (error) {
    console.error('Error in getPresignedDownloadUrl:', error);
    throw error;
  }
};

/**
 * Detects the actual file type by examining the first few bytes of the file
 * This is useful for cases where the file extension doesn't match the actual content
 *
 * @param {string} url - The URL of the file to examine
 * @returns {Promise<string|null>} - The detected MIME type or null if detection failed
 */
export const detectActualFileType = async (url) => {
  try {
    // Fetch just the first few bytes of the file using a range request
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Range': 'bytes=0-11' // First 12 bytes should be enough for most file signatures
      }
    });

    if (!response.ok) {
      console.warn('Range request failed, falling back to Content-Type header');
      return response.headers.get('Content-Type');
    }

    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // Check file signatures (magic numbers)
    // JPEG: Starts with FF D8 FF
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      return 'image/jpeg';
    }

    // PNG: Starts with 89 50 4E 47 0D 0A 1A 0A
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      return 'image/png';
    }

    // GIF: Starts with 47 49 46 38 (GIF8)
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
      return 'image/gif';
    }

    // PDF: Starts with 25 50 44 46 (%PDF)
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return 'application/pdf';
    }

    // HEIC: Typically starts with 00 00 00 xx 66 74 79 70 (....ftyp)
    // where xx is the size, followed by 'ftyp'
    if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
      // Check for HEIC specific brands
      if (bytes[8] === 0x68 && bytes[9] === 0x65 && bytes[10] === 0x69 && bytes[11] === 0x63) {
        return 'image/heic';
      }
      if (bytes[8] === 0x6D && bytes[9] === 0x69 && bytes[10] === 0x66 && bytes[11] === 0x31) {
        return 'image/heif';
      }
      // Generic HEIF container
      return 'image/heif';
    }

    // If we couldn't detect the type from the signature, fall back to the Content-Type header
    return response.headers.get('Content-Type');
  } catch (error) {
    console.error('Error detecting file type:', error);
    return null;
  }
};

/**
 * Opens a file in a new tab using a presigned URL
 *
 * @param {string} fileName - The name of the file to open
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise<void>}
 */
export const openFileInNewTab = async (fileName, onProgress) => {
  const { url } = await getPresignedDownloadUrl(fileName, onProgress);
  window.open(url, "_blank");
};

/**
 * Prepares a file for viewing in a modal
 *
 * @param {string} fileName - The name of the file to view
 * @param {Function} setFileUrl - Function to set the file URL in the component state
 * @param {Function} setFileType - Function to set the file type in the component state
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @param {boolean} detectActualType - Whether to try to detect the actual file type (default: true)
 * @returns {Promise<void>}
 */
export const prepareFileForModal = async (fileName, setFileUrl, setFileType, onProgress, detectActualType = true) => {
  try {
    console.log('Preparing file for modal:', fileName);

    // Get the presigned URL and initial content type based on file extension
    const { url, contentType: initialContentType } = await getPresignedDownloadUrl(fileName, onProgress);

    if (!url) {
      throw new Error('Failed to get presigned URL');
    }

    console.log('Got presigned URL:', url);
    console.log('Initial content type:', initialContentType);

    // Set the URL immediately so the file can start loading
    setFileUrl(url);

    // Set the initial content type based on file extension
    setFileType(initialContentType);

    // If detectActualType is true, try to detect the actual file type
    if (detectActualType) {
      try {
        if (onProgress) onProgress(95);

        // Try to detect the actual file type
        const actualContentType = await detectActualFileType(url);
        console.log('Detected actual content type:', actualContentType);

        if (actualContentType && actualContentType !== initialContentType) {
          console.log(`File type mismatch detected: Extension suggests ${initialContentType} but content is ${actualContentType}`);

          // Special handling for HEIC files that might be named as JPEG
          if (actualContentType.includes('heic') || actualContentType.includes('heif')) {
            console.log('HEIC/HEIF format detected. This may not display correctly in all browsers.');

            // For HEIC files, we might want to use a special viewer or converter
            // For now, we'll just set the correct MIME type
            setFileType(actualContentType);
          } else {
            // For other mismatches, update to the actual content type
            setFileType(actualContentType);
          }
        }
      } catch (error) {
        console.warn('Error detecting actual file type:', error);
        // Keep using the extension-based content type if detection fails
      }
    }

    if (onProgress) onProgress(100);
  } catch (error) {
    console.error('Error preparing file for modal:', error);
    // Set a default content type if there was an error
    setFileType('application/octet-stream');
    if (onProgress) onProgress(100);
    throw error; // Re-throw to be handled by the caller
  }
};

/**
 * Determines MIME type based on file extension
 *
 * @param {string} fileName - The name of the file
 * @returns {string} - The MIME type
 */
export const getMimeTypeFromFileName = (fileName) => {
  const fileExtension = fileName.split('.').pop().toLowerCase();

  switch (fileExtension) {
    // Document formats
    case 'pdf':
      return 'application/pdf';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'txt':
      return 'text/plain';
    case 'csv':
      return 'text/csv';
    case 'html':
    case 'htm':
      return 'text/html';

    // Common image formats
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    case 'tiff':
    case 'tif':
      return 'image/tiff';

    // Apple/iOS specific formats
    case 'heic':
    case 'heif':
      return 'image/heic';
    case 'heics':
      return 'image/heic-sequence';
    case 'avif':
      return 'image/avif';

    // Audio formats
    case 'mp3':
      return 'audio/mpeg';
    case 'wav':
      return 'audio/wav';
    case 'ogg':
      return 'audio/ogg';
    case 'm4a':
      return 'audio/mp4';

    // Video formats
    case 'mp4':
      return 'video/mp4';
    case 'mov':
      return 'video/quicktime';
    case 'avi':
      return 'video/x-msvideo';
    case 'wmv':
      return 'video/x-ms-wmv';
    case 'flv':
      return 'video/x-flv';
    case 'webm':
      return 'video/webm';
    case 'mkv':
      return 'video/x-matroska';

    // Archive formats
    case 'zip':
      return 'application/zip';
    case 'rar':
      return 'application/x-rar-compressed';
    case '7z':
      return 'application/x-7z-compressed';
    case 'tar':
      return 'application/x-tar';
    case 'gz':
    case 'gzip':
      return 'application/gzip';

    // Default for unknown types
    default:
      return 'application/octet-stream';
  }
};
